import { ProjectCharge } from "~/gql/types";
import { EditProjectChargeCommand } from "~/gql/types2";
import { ChargeFormFieldValues } from "../types";

/**
 * Reproduce the charge's label from the form values:
 *   useCustomRate          → customRateLabel   (override / custom label)
 *   rate.invoiceLabel      → the rate's invoice label, if present
 *   rate.name              → the rate's name
 */
export const deriveChargeLabel = (values: ChargeFormFieldValues): string => {
  if (values.useCustomRate) {
    return values.customRateLabel!;
  }

  const rate = values.rate;

  if (rate && "invoiceLabel" in rate && rate.invoiceLabel) {
    return rate.invoiceLabel;
  }

  return rate!.name;
};

/**
 * Pure function: `(values, charge) → EditProjectChargeCommand`.
 *
 * For each mutation field: if the source field is in `liveFields`, take it from
 * the user's `values`; otherwise echo the loaded charge's value (through the
 * seeded value). This is the write half of the round-trip invariant.
 *
 * Applies normalizations:
 *   - date formatted `YYYY-MM-DD`, and omitted (null) when the charge's date is
 *     not editable (`canEditDate === false`)
 *   - `rateSheetName` omitted (undefined) when the charge is an override
 *   - `label` computed from the override / invoice-label / rate-name precedence
 *
 * Throws when there is no EE code rather than sending a blank one.
 */
export const buildChargeMutation = (
  values: ChargeFormFieldValues,
  charge: ProjectCharge
): EditProjectChargeCommand => {
  const notes = values.notes;
  const editNotes = values.editNotes!;
  const billable = values.billable;
  const eeCode = values.eeCode?.eeCode;

  if (!eeCode) {
    throw new Error("An EE code is required to save a charge.");
  }

  return {
    id: charge.id,
    quantity: parseFloat(`${values.quantity}`),
    rate: parseFloat(values.rateAmount),
    label: deriveChargeLabel(values),
    notes,
    editNotes,
    billable,
    eeCode,
    unit: values.unit,
    date: charge.canEditDate ? values.date!.format("YYYY-MM-DD") : null,
    rateSheetName: values.useCustomRate
      ? undefined
      : values.rateSheetName ?? undefined,
    employee: values.employee?.userPrincipalName || null,
    projectId: values.project?.number,
    taxable: values.taxable,
  };
};
